import React, { useEffect, useState, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ShoppingBag, BadgeCheck, X } from 'lucide-react';
import { PLANS } from '../lib/checkout';
const NAMES = ['Sofía G.', 'Mateo P.', 'Valentina R.', 'Joaquín M.', 'Martina L.', 'Benjamín S.', 'Camila A.', 'Bautista F.', 'Catalina V.', 'Tomás D.', 'Emma C.', 'Lautaro B.', 'Lucía H.', 'Thiago N.', 'Olivia E.', 'Santino G.', 'Renata J.', 'Lorenzo P.', 'Isabella M.', 'Felipe Q.', 'Mía T.', 'Bruno A.', 'Delfina R.', 'Nicolás S.', 'Jazmín O.', 'Ignacio C.', 'Agustina B.', 'Lucas V.', 'Victoria F.', 'Franco I.', 'Pilar M.', 'Gonzalo R.', 'Florencia D.', 'Ramiro L.', 'Antonia P.', 'Julián K.'];
const LOCATIONS = ['Buenos Aires', 'Córdoba', 'Rosario', 'Mendoza', 'La Plata', 'Mar del Plata', 'San Miguel de Tucumán', 'Salta', 'Santa Fe', 'San Juan', 'Resistencia', 'Neuquén', 'Corrientes', 'Posadas', 'Bahía Blanca', 'Paraná', 'Formosa', 'Río Gallegos', 'Ushuaia', 'San Luis', 'La Rioja', 'Catamarca', 'Santiago del Estero', 'Jujuy', 'Comodoro Rivadavia', 'Trelew', 'Bariloche', 'Tandil', 'Rafaela', 'Concordia', 'Quilmes', 'Lanús', 'Avellaneda', 'San Isidro', 'Pilar', 'Tigre'];
const TIME_AGO = ['ahora mismo', 'hace 1 minuto', 'hace 2 minutos', 'hace 3 minutos', 'hace 5 minutos', 'hace 7 minutos'];
type Plan = (typeof PLANS)[keyof typeof PLANS];
interface Notification {
  name: string;
  location: string;
  plan: Plan;
  timeAgo: string;
  key: number;
}
function pick<T>(arr: T[]): T {
  return arr[Math.floor(Math.random() * arr.length)];
}
function generateNotification(): Notification {
  // Weight: ~65% TOTAL, ~35% basic (TOTAL is "more elegido")
  const plan = Math.random() < 0.65 ? PLANS.total : PLANS.basic;
  return {
    name: pick(NAMES),
    location: pick(LOCATIONS),
    plan,
    timeAgo: pick(TIME_AGO),
    key: Date.now() + Math.random()
  };
}
export function PurchaseNotification() {
  const [notification, setNotification] = useState<Notification | null>(null);
  const [dismissed, setDismissed] = useState(false);
  const timersRef = useRef<ReturnType<typeof setTimeout>[]>([]);
  useEffect(() => {
    if (dismissed) return;
    const clearTimers = () => {
      timersRef.current.forEach(clearTimeout);
      timersRef.current = [];
    };
    const showCycle = (initialDelay: number) => {
      const showTimer = setTimeout(() => {
        setNotification(generateNotification());
        // Hide after 5.5s, then wait 40-60s before showing the next one
        const hideTimer = setTimeout(() => {
          setNotification(null);
          const nextDelay = 40000 + Math.random() * 20000; // 40-60s
          showCycle(nextDelay);
        }, 5500);
        timersRef.current.push(hideTimer);
      }, initialDelay);
      timersRef.current.push(showTimer);
    };
    // First notification appears 8s after page load
    showCycle(8000);
    return clearTimers;
  }, [dismissed]);
  if (dismissed) return null;
  return <div className="fixed bottom-28 md:bottom-6 left-3 md:left-6 z-40 pointer-events-none" data-id="element-219">
      <AnimatePresence mode="wait" data-id="element-220">
        {notification && <motion.div key={notification.key} initial={{
        opacity: 0,
        x: -20,
        y: 10,
        scale: 0.95
      }} animate={{
        opacity: 1,
        x: 0,
        y: 0,
        scale: 1
      }} exit={{
        opacity: 0,
        x: -20,
        scale: 0.95
      }} transition={{
        duration: 0.3,
        ease: 'easeOut'
      }} className="pointer-events-auto bg-white rounded-xl shadow-2xl border border-border max-w-[300px] md:max-w-[340px] overflow-hidden" data-id="element-221">
            <div className="flex items-stretch" data-id="element-222">
              {/* Avatar/Icon column */}
              <div className="flex-shrink-0 bg-gradient-to-br from-blue to-navy w-12 md:w-14 flex items-center justify-center relative" data-id="element-223">
                <ShoppingBag className="text-gold" size={20} strokeWidth={2} data-id="element-224" />
                <motion.div animate={{
              scale: [1, 1.3, 1],
              opacity: [1, 0, 1]
            }} transition={{
              duration: 1.5,
              repeat: Infinity
            }} className="absolute top-1.5 right-1.5 w-1.5 h-1.5 bg-success rounded-full" data-id="element-225" />
              </div>

              {/* Content */}
              <div className="flex-1 p-3 md:p-3.5 pr-2 min-w-0" data-id="element-226">
                <div className="flex items-center gap-1.5 mb-0.5" data-id="element-227">
                  <BadgeCheck size={13} className="text-success flex-shrink-0" fill="currentColor" color="white" strokeWidth={2.5} data-id="element-228" />
                  <span className="text-[9px] md:text-[10px] font-extrabold text-success uppercase tracking-wider" data-id="element-229">
                    Compra verificada
                  </span>
                </div>
                <p className="text-[12px] md:text-[13px] text-text-main leading-snug" data-id="element-230">
                  <strong className="font-bold text-navy" data-id="element-231">
                    {notification.name}
                  </strong>
                  <span className="text-text-muted" data-id="element-232">
                    {' '}
                    de {notification.location}
                  </span>
                </p>
                <p className="text-[11px] md:text-[12px] text-text-muted leading-snug mt-0.5" data-id="element-233">
                  compró{' '}
                  <strong className={notification.plan.featured ? 'text-gold font-bold' : 'text-blue font-bold'} data-id="element-234">
                    {notification.plan.name}
                  </strong>
                </p>
                <p className="text-[10px] md:text-[11px] text-text-muted/70 mt-1" data-id="element-235">
                  ⏱️ {notification.timeAgo}
                </p>
              </div>

              {/* Close button */}
              <button onClick={() => setDismissed(true)} className="flex-shrink-0 self-start p-1.5 text-text-muted hover:text-navy transition-colors" aria-label="Cerrar" data-id="element-236">
                <X size={14} data-id="element-237" />
              </button>
            </div>
          </motion.div>}
      </AnimatePresence>
    </div>;
}